import { HttpRequest, HttpEvent, HttpErrorResponse, HttpInterceptor, HttpHandler } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { from, Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, finalize, filter, take } from 'rxjs/operators';
import { ENVIRONMENT_TOKEN } from '../../core/injection-tokens/environment.token';
import { AuthStore } from 'projects/shared/src/lib/store/auth.store';
import { Environment } from '../../core/interfaces/environment';
import { AuthInfo } from '../../core/interfaces/auth-info.interface';
import { LocalStorageService, SELECTED_LANG } from '../../core/services/local-storage.service';

@Injectable()
export class CommonHttpInterceptor implements HttpInterceptor {
  private readonly authStore = inject(AuthStore);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly environment: Environment = inject(ENVIRONMENT_TOKEN);
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<AuthInfo | null> = new BehaviorSubject<AuthInfo | null>(null);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.includes(`${this.environment.baseApiUrl}`)) {
      return next.handle(request);
    }

    if (this.authStore.isAuthenticated()) {
      return this.handleAuthorizedRequest(next, request)
    }

    return from(this.authStore.refreshAuthInfo())
      .pipe(
        take(1),
        switchMap(() => this.handleAuthorizedRequest(next, request))
      );
  }

  private handleAuthorizedRequest(next: HttpHandler, request: HttpRequest<any>): Observable<HttpEvent<any>> {
    return next.handle(this.attachToken(request, this.authStore.authInfo.accessToken()))
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            return this.handle401Error(request, next);
          } else if (error.status === 403) {
            if (request.url.includes(`api/v1/account/current`)) {
              return from(this.authStore.centralLogout())
                .pipe(switchMap(() => throwError(() => `No ${this.environment.projectName} license for current user`)))
            }
          }
          return throwError(() => error);
        })
      );
  }

  private attachToken(request: HttpRequest<any>, accessToken: string): HttpRequest<any> {
    const currentLang = this.getCurrentLang();
    if (accessToken) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
          'Accept-Language': currentLang,
        },
      });
    }
    return request;
  }

  private getCurrentLang(): string {
    const userId = this.authStore.account()?.identityId;

    if (userId) {
      const selectedLang = this.localStorageService.getUserScopedItem(SELECTED_LANG, userId);
      if (selectedLang) {
        return selectedLang;
      }
    }

    const persistentLang = this.localStorageService.getPersistentItem(SELECTED_LANG);
    if (persistentLang) {
      return persistentLang;
    }

    return 'nl';
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log('Token refresh process started due to 401 response code');

    // If token refresh is already in progress, wait for it to complete
    if (this.refreshTokenInProgress) {
      console.log('Token refresh already in progress, queuing request');
      return this.refreshTokenSubject.pipe(
        filter(authInfo => authInfo !== null),
        take(1),
        switchMap((authInfo) => {
          if (authInfo && authInfo.isAuthenticated && authInfo.accessToken) {
            console.log('Using refreshed token from queued request');
            return next.handle(this.attachToken(request, authInfo.accessToken));
          }
          return throwError(() => new Error('Token refresh failed'));
        })
      );
    }

    // Start new token refresh
    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    return from(this.authStore.refreshToken())
      .pipe(
        filter(x => x.isAuthenticated),
        take(1),
        switchMap((authInfo) => {
          if (authInfo.isAuthenticated && authInfo.accessToken) {
            console.log('Token refresh successful');
            this.refreshTokenSubject.next(authInfo);
            return next.handle(this.attachToken(request, authInfo.accessToken));
          } else {
            console.log('Token refresh failed, initiating authorization');
            this.refreshTokenSubject.next(null);
            return from(this.authStore.logout())
              .pipe(
                switchMap(() => throwError(() => new Error('Token refresh failed - redirected to authorization')))
              );
          }
        }),
        catchError((error) => {
          console.error('Token refresh FINAL error:', error);
          this.refreshTokenSubject.next(null);
          return throwError(() => error);
        }),
        finalize(() => {
          console.log('Token refresh process completed');
          this.refreshTokenInProgress = false;
        })
      );
  }
}
